import { Button } from "components/Button/Button";
import { IconButton } from "components/Button/IconButton";
import { Tooltip } from "components/Tooltip/Tooltip";
import { IconClockHistory } from "components/Icons/IconClockHistory";
import { Modal } from "components/Modal";
import { ControlledModal } from "components/Modal";
import {
  ScheduleDashboardExecutionModalContent,
  FormState,
} from "shared/ScheduleExecutionModalContent";
import { ScheduleExecutionUpsellModalContent } from "shared/ScheduleExecutionUpsellModalContent";
import { ScheduleDashboardFeatureHighlight } from "shared/TeamsAnnouncement/TeamFeatureHighlights";

import { useSessionWithUser } from "gui/session/session";
import { useGetRefreshDashboardArguments } from "../useGetRefreshDashboardArguments";
import {
  useDashboardSchedule,
  DashboardSchedule,
} from "./useDashboardSchedule";
import { useCreateDashboardSchedule } from "./useCreateDashboardSchedule";
import { useUpdateDashboardSchedule } from "./useUpdateDashboardSchedule";
import { useDeleteDashboardSchedule } from "./useDeleteDashboardSchedule";
import {
  toRateOption,
  toButtonText,
} from "shared/ScheduleExecutionModalContent/cron/optionsRate";
import { useScheduleContextOwner } from "./useScheduleContextOwner";
import { useEditorTheme } from "gui/editor/context";
import { useState } from "react";
import { useAnalytics } from "gui/analytics/analytics";
import { useHasEditPermission } from "lib/permissions/permissions";

import type { ActiveContext } from "shared/ContextSwitcher/ActiveContext";
import type { SessionWithUser } from "lib/users/types";
import type { ExecutionTiersData } from "shared/SelectPerformance/useGetPerformanceTiers";
import type { FullDashboard } from "lib/entries/types";

interface Props {
  dashboard: FullDashboard;
}

export function ScheduleDashboardExecutionButton(props: Props) {
  const session = useSessionWithUser();
  const canEdit = useHasEditPermission(props.dashboard.owner);
  const isArchived = props.dashboard.is_archived;

  if (!session || !canEdit || isArchived) {
    return null;
  }

  return <LoggedIn session={session} dashboard={props.dashboard} />;
}

function LoggedIn(props: {
  session: SessionWithUser;
  dashboard: FullDashboard;
}) {
  const { session, dashboard } = props;

  const theme = useEditorTheme();
  const [isOpen, setIsOpen] = useState(false);
  const analytics = useAnalytics();

  const existingSchedule = useDashboardSchedule(session, dashboard.id);

  const contextOwner = useScheduleContextOwner(
    session,
    existingSchedule,
    dashboard.owner
  );

  const { queryIds, performanceTiers } = useGetRefreshDashboardArguments(
    dashboard.visualization_widgets || [],
    contextOwner
  );

  if (!contextOwner) {
    // If user can edit the current dashboard, contextOwner should resolve
    // as either the user or a team they're in.
    return null;
  }

  const option = existingSchedule && toRateOption(existingSchedule.cronString);

  if (performanceTiers.length === 0) {
    return (
      <ControlledModal
        isOpen={isOpen}
        setIsOpen={(isOpen) => {
          if (isOpen) {
            analytics.captureEvent("Refresh dashboard upsell modal: Appear");
          } else {
            analytics.captureEvent("Refresh dashboard upsell modal: Closed");
          }
          setIsOpen(isOpen);
        }}
        size="M"
        label="Schedule dashboard refresh"
        content={({ close }) => (
          <ScheduleExecutionUpsellModalContent close={close} />
        )}
        trigger={({ isOpen, onClick }) => (
          <ScheduleDashboardFeatureHighlight>
            <Tooltip
              position="above-center"
              darkMode={theme === "dark"}
              label="Upgrade to enable dashboard refresh"
            >
              <IconButton
                size="M"
                theme="tertiary"
                className={isOpen ? "active" : undefined}
                onClick={onClick}
              >
                <IconClockHistory />
              </IconButton>
            </Tooltip>
          </ScheduleDashboardFeatureHighlight>
        )}
      />
    );
  }

  return (
    <Modal
      size="M"
      label="Dashboard execution schedule"
      content={({ close }) => (
        <ModalContentContainer
          session={session}
          contextOwner={contextOwner}
          performanceTiers={performanceTiers}
          dashboard={dashboard}
          queryCount={queryIds.length}
          existingSchedule={existingSchedule}
          close={close}
        />
      )}
      trigger={({ isOpen, onClick }) => (
        <ScheduleDashboardFeatureHighlight>
          <Tooltip
            position="above-center"
            darkMode={theme === "dark"}
            label="Schedule dashboard refresh"
          >
            {existingSchedule ? (
              <Button
                size="M"
                theme="tertiary"
                className={isOpen ? "active" : undefined}
                onClick={onClick}
              >
                <IconClockHistory />
                {option === undefined ? "Unknown" : toButtonText(option)}
              </Button>
            ) : (
              <IconButton
                size="M"
                theme="tertiary"
                className={isOpen ? "active" : undefined}
                onClick={onClick}
              >
                <IconClockHistory />
              </IconButton>
            )}
          </Tooltip>
        </ScheduleDashboardFeatureHighlight>
      )}
    />
  );
}

/*
 * This subcomponent is only rendered when the modal is open. Using the
 * mutation hooks here makes the mutation result state reset when
 * closing and reopening the modal.
 */
function ModalContentContainer(props: {
  session: SessionWithUser;
  contextOwner: ActiveContext;
  performanceTiers: ExecutionTiersData[];
  dashboard: FullDashboard;
  queryCount: number;
  existingSchedule: DashboardSchedule | undefined;
  close: () => void;
}) {
  const {
    session,
    contextOwner,
    performanceTiers,
    dashboard,
    queryCount,
    existingSchedule,
    close,
  } = props;

  const [
    createDashboardSchedule,
    createDashboardScheduleResult,
  ] = useCreateDashboardSchedule();

  const [
    updateDashboardSchedule,
    updateDashbaordScheduleResult,
  ] = useUpdateDashboardSchedule();

  const [
    deleteDashboardSchedule,
    deleteDashboardScheduleResult,
  ] = useDeleteDashboardSchedule();

  const onSave = (state: FormState) => {
    if (existingSchedule) {
      updateDashboardSchedule(
        session,
        { dashboardId: dashboard.id, scheduleId: existingSchedule.id },
        state
      ).then(close);
    } else {
      createDashboardSchedule(session, dashboard.id, state).then(close);
    }
  };

  const onStop = existingSchedule
    ? () =>
        deleteDashboardSchedule(session, {
          dashboardId: dashboard.id,
          scheduleId: existingSchedule.id,
        }).then(close)
    : undefined;

  return (
    <ScheduleDashboardExecutionModalContent
      contextOwner={contextOwner}
      performanceTiers={performanceTiers}
      queryCount={queryCount}
      initialState={existingSchedule}
      save={onSave}
      stop={onStop}
      close={close}
      somethingWentWrong={Boolean(
        createDashboardScheduleResult.error ||
          deleteDashboardScheduleResult.error ||
          updateDashbaordScheduleResult.error
      )}
    />
  );
}
